import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'axios';

import { ExportTable, DestroyTable /* , FormatDate */ } from '../../utils/Functions';
import Table from '../Table';
import InfoPanel from '../Panels/InfoPanel';
import MiniButton from '../Buttons/MiniButton';
import iziQuestion from '../IziToast/question';
import IziToast from '../IziToast';

// Soft pill palette per statement type — matches the visual language used on
// the Properties / Suspense pages so things feel consistent.
const TYPE_PILL = {
  Rent: { bg: '#eef1f4', color: '#5b6b7c' },
  'Water Bill': { bg: '#e6f4f9', color: '#1e6e8c' },
  'Service Charge': { bg: '#e8f6ee', color: '#2e7d4f' },
  'Extra Charge': { bg: '#fdf3e3', color: '#b9831e' },
  Commission: { bg: '#f5f5f5', color: '#888' },
  'Opening Balance': { bg: '#211611', color: '#fff' },
};

const TypePill = ({ type }) => {
  if (!type) return <span style={{ color: '#bbb' }}>—</span>;
  const palette = TYPE_PILL[type] || { bg: '#f5f5f5', color: '#666' };
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 11,
      fontWeight: 600,
      padding: '0.28em 0.6em',
      borderRadius: 10,
      background: palette.bg,
      color: palette.color,
    }}
    >
      {type}
    </span>
  );
};

// Status pill — mirrors the Suspense Account aesthetic so the visual
// language stays consistent. `paid` / `fully_paid` go green, anything
// partial is amber, unpaid is muted, anything else (e.g. "cancelled")
// falls through to a neutral grey.
const STATUS_PILL = {
  paid: { bg: '#e6f4ea', color: '#1e7e34', label: 'Paid' },
  fully_paid: { bg: '#e6f4ea', color: '#1e7e34', label: 'Paid' },
  partially_paid: { bg: '#fdf3e3', color: '#b9831e', label: 'Partial' },
  unpaid: { bg: '#fdecea', color: '#a4282d', label: 'Unpaid' },
};

const StatusPill = ({ status }) => {
  if (!status) return <span style={{ color: '#bbb' }}>—</span>;
  const meta = STATUS_PILL[status] || {
    bg: '#f1f3f5', color: '#5b6b7c', label: status,
  };
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 11,
      fontWeight: 600,
      padding: '0.28em 0.6em',
      borderRadius: 10,
      background: meta.bg,
      color: meta.color,
    }}
    >
      {meta.label}
    </span>
  );
};

// "KES 1,234" — the same convention used on the cream-card invoice
// design so users see the currency unit on every money cell.
const fmtMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num === 0) return 'KES 0';
  return `KES ${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

// Base URL for the public PDF routes. Strips a trailing slash so we
// don't end up with `//public/...` if REACT_APP_API_URL ends with /.
const PDF_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');

// Open the invoice or receipt PDF in a new tab. The public PDF routes
// don't require auth (they validate the ref UUID prefix server-side),
// so a plain window.open is enough — no auth header needed.
const openPdf = (kind, ref) => {
  if (!ref) return;
  const path = kind === 'receipt' ? 'receipt.pdf' : 'invoice.pdf';
  window.open(`${PDF_BASE}/public/${path}?ref=${ref}`, '_blank', 'noopener');
};

const pdfLinkStyle = {
  color: '#d73836',
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};

const MonthlyReportTable = ({
  isLoading, error, arrears, user, typeFilter,
}) => {
  // token
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };
  // refs
  const tableRef = React.useRef(null);

  // Apply the per-Type filter from the parent toolbar. Empty → all types.
  const filteredArrears = React.useMemo(() => {
    if (!typeFilter) return arrears;
    return (arrears || []).filter((r) => r.type === typeFilter);
  }, [arrears, typeFilter]);

  // life-cycle hooks — destroy DataTable before swapping rows (avoid the
  // React-vs-jQuery insertBefore crash) then re-init when the next paint
  // settles. typeFilter is in the deps so changing the toolbar's Type
  // refreshes the DataTable cleanly.
  React.useEffect(() => {
    DestroyTable('arrears_table');
    if (filteredArrears.length > 0) {
      const t = window.setTimeout(() => {
        ExportTable('arrears_table', 'Account Statement');
      }, 50);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [isLoading, filteredArrears]);


  return isLoading ? (
    <InfoPanel message="Loading..." />
  ) : error ? (
    <InfoPanel message="An error occurred" hasError />
  ) : (
    <>
      {filteredArrears && filteredArrears.length ? (
        <Table tableId="arrears_table" marginTop={10} tableRef={tableRef}>
          <thead>
            <tr>
              <th>No.</th>
              <th style={{ maxWidth: '40px' }}>Invoice Date</th>
              <th>Property</th>
              <th>Hse Unit.</th>
              <th>Invoice No.</th>
              <th>Type</th>
              <th>Status</th>
              <th>Description</th>
              <th>Payment Date</th>
              <th>Credit</th>
              <th>Debit</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {filteredArrears.map((arrear, id) => {
              let index = id;
              const date = arrear.date;
              const payment_date = arrear.paymentDate;
              const propertyName = arrear.property;
              const hse_unit = arrear.houseUnit;
              const invoice_number = arrear.invoiceNo;
              const receipt_number = arrear.receiptNo;
              const description = arrear.description;
              const credit = arrear.credit;
              const debit = arrear.debit;
              // Negative balance ⇒ tenant owes us; render in red with
              // parentheses (the accounting convention) so arrears jump
              // out visually. Zero / positive are neutral / green.
              const rawBal = Number(arrear.balance) || 0;
              const balanceColor = rawBal < 0 ? '#a4282d' : (rawBal > 0 ? '#1e7e34' : '#5b6b7c');
              const balanceText = rawBal < 0
                ? `(${fmtMoney(-rawBal)})`
                : fmtMoney(rawBal);
              // A "debit row" represents an actual payment received against
              // the invoice — those are the rows where a receipt exists
              // and we can offer the receipt PDF link. Top entries (credit
              // rows) don't have a payment, only an invoice.
              const isPaymentRow = !!receipt_number && Number(debit) > 0;
              // The synthetic "Balance brought forward" row anchors the
              // ledger to the payer's position as of the range start.
              // Give it a faint highlight so it reads as a divider, not a
              // transaction.
              const isOpening = !!arrear.isOpening;

              return (
                <tr key={id} style={isOpening ? { background: '#faf7f2' } : undefined}>
                  <td style={{ width: '5px' }}>{isOpening ? '' : ++index}</td>
                  <td>{date}</td>
                  <td>{propertyName}</td>
                  <td>{hse_unit}</td>
                  <td>
                    {invoice_number ? (
                      <a
                        href={`${PDF_BASE}/public/invoice.pdf?ref=${invoice_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View invoice PDF"
                        style={pdfLinkStyle}
                        onClick={(e) => {
                          // Use window.open so middle-click + ctrl-click
                          // still work via the anchor's href, but a plain
                          // click opens in a new tab via our helper too.
                          e.preventDefault();
                          openPdf('invoice', invoice_number);
                        }}
                      >
                        <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                        {invoice_number}
                      </a>
                    ) : '—'}
                  </td>
                  <td><TypePill type={arrear.type} /></td>
                  <td><StatusPill status={arrear.status} /></td>
                  <td>{description}</td>
                  <td>
                    {payment_date || <span style={{ color: '#bbb' }}>—</span>}
                    {isPaymentRow && (
                      <a
                        href={`${PDF_BASE}/public/receipt.pdf?ref=${receipt_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`View receipt ${receipt_number}`}
                        style={{ ...pdfLinkStyle, marginLeft: 8 }}
                        onClick={(e) => {
                          e.preventDefault();
                          openPdf('receipt', receipt_number);
                        }}
                      >
                        <i className="fa fa-file-pdf-o" />
                        {' '}
                        Receipt
                      </a>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', color: Number(credit) > 0 ? '#1e7e34' : '#bbb' }}>
                    {Number(credit) > 0 ? fmtMoney(credit) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: Number(debit) > 0 ? '#a4282d' : '#bbb' }}>
                    {Number(debit) > 0 ? fmtMoney(debit) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: balanceColor, fontWeight: 600 }}>
                    {balanceText}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <InfoPanel message={
          typeFilter
            ? `No ${typeFilter} entries in the current statement.`
            : 'Seems no records found'
        }
        />
      )}
    </>
  );
};

MonthlyReportTable.propTypes = {
  arrears: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  typeFilter: PropTypes.string,
};

MonthlyReportTable.defaultProps = {
  error: null,
  typeFilter: '',
};

TypePill.propTypes = {
  type: PropTypes.string,
};

StatusPill.propTypes = {
  status: PropTypes.string,
};

StatusPill.defaultProps = {
  status: '',
};

TypePill.defaultProps = {
  type: '',
};

export default connect((state) => {
  return state;
})(MonthlyReportTable);
